import 'ol/ol.css';
import OLMap from 'ol/Map';
import { fromLonLat } from 'ol/proj';
import View from 'ol/View';

import Mediator from 'gisw/Classes/Mediator';
// @ts-ignore
import auth from 'gisw/Helpers/auth';
import MapLayerCredientials from 'gisw/Helpers/MapLayerCredientials';
import { getValue } from 'gisw/Helpers/storage';
import GWindowList from 'gisw/Widgets/GWindowList';
import GWindowManager from 'gisw/Widgets/GWindowManager';
import screenSizeWarning from 'gisw/Widgets/MessageManager/screenSizeWarning';

import updateCheck from 'src/update-check';

import Data from './Data';
import { getHandlers } from './Handlers';
import BaseMapManager from './Widgets/BasemapManager';
import BottomRightMenu from './Widgets/BottomRightMenu';
import CenterMenu from './Widgets/CenterMenu';
import LayersManager from './Widgets/LayersManager';
import RightMenu from './Widgets/RightMenu';
import ToolsManager from './Widgets/ToolsManager';

/**
 * @module eCounty/Main
 *
 * @description
 * This module contains _ALL_ initialization logic for eCounty sites.  This
 * excludes any js that is generated by the server; see
 * Views/eCounty/Index.cshtml.
 */
define([], function () {
  'use strict';

  const mobileLeftContainer = 'mobile-left-container';
  const mobileRightContainer = 'mobile-right-container';

  return {
    /**
     * @param {object} config
     * @param {object} config.serverData
     */
    async init (config) {
      Mediator.constructor({ serverData: config.serverData });
      await _init();

      // Start watching for updates
      updateCheck();
    }
  };

  /**
   * @function _init
   * @private
   * @memberof module:eCounty/Main
   * @description
   * the main entry point for eCounty sites.  Prior to the execution of
   * this function, the {@link Mediator} instance has been created and
   * site specific data has been added by the server;
   * see `Views/eCounty/Index.cshtml`.
   * Note that the {@link Mediator#store} method automatically calls
   * the `startup` method when storing a widget.
   */
  async function _init () {
    // @ts-ignore
    window.console.errorOld = window.console.error;

    window.console.error = function (text, ...others) {
      // @ts-ignore
      window.console.errorOld(text, ...others);

      if (text?.message && text?.stack) {
        Mediator.jstManager.jstError(
          'Console Error with stack',
          text.message,
          null,
          text.stack,
          null,
          null
        );
      } else {
        Mediator.jstManager.jstError(
          'Console Error',
          '',
          null,
          text,
          null,
          null
        );
      }

      // Temporary until dojo-webpack-plugin is removed
      // Immediately check for updates on ChunkLoadErrors
      if (/^ChunkLoadError/i.test(text?.name || text)) {
        updateCheck();
      }
    };

    window.onerror = function (message, url, line, col, error) {
      const errorStack = error?.stack;
      Mediator.jstManager.jstError(
        'Global Error',
        message,
        null,
        error ? JSON.stringify(error) : '',
        null,
        null
      );
      return false;
    };

    // Work around until this issue is fixed
    // https://bugs.chromium.org/p/chromium/issues/detail?id=1072475
    (function ($) {
      $.fn.mSort = function (callback) {
        const children = this.children().sort(callback);

        for (let j = 0; j < this.length; j++) {
          for (let i = 0; i < children.length; i++) {
            this[j].appendChild(children[i]);
          }
        }

        this.find('optgroup').each(function (_, group) {
          const children = $(group).children().sort(callback);

          for (let i = 0; i < children.length; i++) {
            group.appendChild(children[i]);
          }
        });

        return this;
      };
    })(jQuery);

    $.ajaxSetup({ cache: false });

    Mediator.mapLayerCredientials = new MapLayerCredientials();
    window.addEventListener('keydown', function (e) {
      // ctrl key + g pressed, check for streetView tool
      if (e.ctrlKey && e.keyCode === 71) {
        if (
          Mediator.mapPoint &&
          Mediator.toolsManager.hasTool(
            'StreetView',
            Mediator.getCurrentTabName()
          )
        ) {
          if (Mediator.serverData) {
            Mediator.jstManager.jstToolEvent(
              'StreetView',
              'Shortcut Used',
              'Street View',
              Mediator.mapPoint.latitude,
              Mediator.mapPoint.longitude
            );
          } else {
            Mediator.jstManager.jstError(
              'StreetView',
              'Shortcut Used',
              'Street View',
              'Mediator.serverData not defined',
              Mediator.mapPoint.latitude,
              Mediator.mapPoint.longitude
            );
          }

          e.stopPropagation();

          const streetViewURL =
            'https://maps.google.com/maps?q=&layer=c&cbll=' +
            Mediator.mapPoint.latitude +
            ',' +
            Mediator.mapPoint.longitude;
          window.open(streetViewURL);
        }
        // ctrl key + l pressed, check for streetView tool
      } else if (e.ctrlKey && e.keyCode === 76) {
        Mediator.bottomRightMenu.copyCoordsToClipboard();
      }
    });

    // Use this module/method to define 'global' data/methods on Mediator
    // in addition to the widgets loaded below.
    Data.preProcess();

    Mediator.destroyWidgets = () => {
      _destroyWidgets();
    };

    /* initializes auth module */
    Mediator.store('login', await _loadLoginPanel(), false);

    // Show login screen when there's no tab to show.
    const showLoginPage =
      (auth.siteLoginRequired || auth.permissionRequiredForTab) &&
      (!auth.loggedIn || Mediator.serverData.Tabs.length === 0);

    if (showLoginPage) {
      Mediator.login.show();
    } else {
      await _LoginInit();
    }

    if (auth.loggedIn) {
      Mediator.initChurnZero();
    }
  }

  /**
   * @param {*} [loginInfo]
   */
  async function _LoginInit (loginInfo = null) {
    try {
      await Mediator.mapLayerCredientials.getMapLayerAuthenticated('submap');
    } catch (error) {
      Mediator.jstManager.jstError('Widget', 'Main', '_LoginInit', error);
    }

    if (Mediator.toolsManager) {
      Mediator.toolsManager.closeActiveTool(true);
    }

    if (Mediator.hasWidgetsToDelete()) {
      _destroyWidgets();
    }

    if (loginInfo) {
      // user has logged in, update this info
      Mediator._processLoginData(loginInfo);
    }

    Mediator.currentTab = null;

    await _reloadWidgets();

    Mediator.ClearGraphics(true, true);
  }

  async function _reloadWidgets () {
    const showDisclaimer = needsDisclaimer();
    const reloaded = Mediator.map ? true : false;
    Mediator.startupWidgetsIds = _getCurRegisteredWidgetsIds();

    if (
      showDisclaimer &&
      (!Mediator.helppage || Mediator.helppage.isDestroyed)
    ) {
      Mediator.store('helppage', await _loadDisclaimer());

      if (reloaded || Mediator.serverData.FrontEndOption.NoDisclaimer) {
        Mediator.helppage.hide();
      }
    }

    if (Mediator.helppage) {
      Mediator.helppage.initTabContainers();
    }

    /**
     * Tools that have methods which need the map/layers to be loaded should
     * be created here, and their methods can be called in the callback below.
     */
    Mediator.store('toolsManager', _loadToolManager());

    if (!reloaded) {
      Mediator.map = _loadMap();

      _bootUpAlwaysOnIdentify();
      Mediator.store('baseMapManager', _loadBaseMapManager(), false);
      Mediator.store('layermanager', _loadLayersManager(), false);
    }

    // Some counties have departments that
    // only show up after user logs in
    Mediator.centerMenu = new CenterMenu({
      id: 'centerMenu',
      display: 'all'
    });

    const [rightMenu, resultsPanel, payment] = await Promise.all([
      _loadRightMenu(),
      _loadResultsPanel(),
      _loadPayment()
    ]);

    Mediator.store('bottomRightMenu', _loadBottomRightMenu());
    Mediator.store('rightMenu', rightMenu);
    Mediator.store('resultsPanel', resultsPanel);
    Mediator.store('paymentPanel', payment);

    Mediator.store('CartPanel', await _loadCart());
    Mediator.store('leftPanel', _loadLeftPanel());
    Mediator.store('gWindowManager', _loadGWindowManager());

    Mediator.registerHandlers(getHandlers());

    Data.postProcess();

    if (Mediator.login?.returnPageIndex) {
      Mediator.login.returnPageIndex = null;
    }

    Mediator.currentToolGroup = null;

    if (Mediator.isMobile || Mediator.inMobileMode) {
      setupMobile();
    }

    if (
      window.innerWidth < 1024 &&
      !Mediator.isMobileDevice &&
      Mediator.showTabletOption
    ) {
      addTabletMode();
    } else {
      removeTabletMode();
    }

    Mediator.hideMobileLeft = hideMobileLeft;
    Mediator.hideMobileRight = hideMobileRight;
    Mediator.showMobileRight = showMobileRight;
    Mediator.showMobileLeft = showMobileLeft;

    if (
      (Mediator.showDesktopMessage || Mediator.showDesktopWarning) &&
      !Mediator.inMobileMode
    ) {
      showDesktopMessage();
    }

    // Adjusts positioning of disclaimer on rotation
    window.addEventListener('resize', function () {
      // don't need to refresh, this causes errors in the top panel
      if (!Mediator.isMobile) {
        if (Mediator.inMobileMode && !Mediator.tabletMode) {
          revertToDesktop();
        }
      } else {
        if (
          !Mediator.inMobileMode &&
          (!Mediator.desktopMode || window.innerWidth <= 750)
        ) {
          setupMobile();
        }
      }

      Mediator.showDesktopMessage = false;

      if (
        window.innerWidth < 1024 &&
        !Mediator.isMobileDevice &&
        Mediator.showTabletOption
      ) {
        addTabletMode();
        Mediator.showDesktopMessage = true;
      } else {
        removeTabletMode();
      }

      if (Mediator.showDesktopMessage) {
        showDesktopMessage();
      }

      if (window.innerWidth >= 1024) {
        $('#ScreenSizeWarningModal').modal('hide');
      }
    });

    Mediator.toolsManager.loadMapNavigation();
    Mediator.handle({ name: 'loadStartUpTool', args: [] });
  }

  function addTabletMode () {
    if (document.getElementById('tabletModeButton') || Mediator.inMobileMode) {
      return;
    }

    // Move loading container into secondary container
    const loading = document.getElementById('loadingContainer');

    if (loading) {
      document.getElementById('loadingContainerSecondary').appendChild(loading);
    }

    const tabletModeButton = document.createElement('button');
    tabletModeButton.textContent = 'Mobile Mode';
    tabletModeButton.id = 'tabletModeButton';
    tabletModeButton.classList.add('help-container-button');

    const helpButtonContainer = document.getElementById('helpButtonContainer');
    helpButtonContainer.insertBefore(
      tabletModeButton,
      helpButtonContainer.firstChild
    );

    tabletModeButton.addEventListener('click', function () {
      Mediator.tabletMode = true;
      Mediator.inMobileMode = true;
      Mediator.desktopMode = false;
      Mediator.showDesktopOption = true;
      setupMobile();
    });
  }

  function removeTabletMode () {
    const tabletModeButton = document.getElementById('tabletModeButton');

    if (!tabletModeButton) {
      return;
    }

    // Move loading container into main container
    const loading = document.getElementById('loadingContainer');
    const loadingContainer = document.getElementById('loadingContainerMain');

    if (loading) {
      loadingContainer.appendChild(loading);
    }

    const loginContainer = document.getElementById('loginButtonContainer');
    loginContainer.insertBefore(loadingContainer, loginContainer.firstChild);

    tabletModeButton.remove();
  }

  function setupMobile () {
    const curEntityDisplayName = Mediator.serverData.displayName.toString();
    Mediator.inMobileMode = true;

    if (Mediator.showDesktopMessage && !Mediator.isMobileDevice) {
      showDesktopMessage();
    }

    if (Mediator.helppage) {
      Mediator.helppage.showDesktopTabs(false);
    }

    // Move loading container into main container
    const loading = document.getElementById('loadingContainer');
    const loadingMain = document.getElementById('loadingContainerMain');

    if (loading) {
      loadingMain.appendChild(loading);
    }

    document.body.appendChild(loadingMain);

    loadingMain.classList.add('mobileMode');

    Mediator.centerMenu.domNode.classList.add('mobile');

    const helpTabContainer = document.getElementById('helpTabsContainer');

    if (helpTabContainer) {
      helpTabContainer.classList.add('mobile');
    }

    const tabletModeButton = document.getElementById('tabletModeButton');

    if (tabletModeButton) {
      tabletModeButton.remove();
    }

    document.getElementById('logo').style.visibility = 'hidden';
    document.getElementById('BottomRightMenu').style.visibility = 'hidden';

    let mobileLeft = document.getElementById(mobileLeftContainer);

    if (!mobileLeft) {
      mobileLeft = document.createElement('div');
      mobileLeft.id = mobileLeftContainer;
      mobileLeft.classList.add('mobilePanel');
      document.body.appendChild(mobileLeft);
    }

    const helpButtons = document.getElementById('helpButtonContainer');

    while (mobileLeft.firstChild) {
      mobileLeft.removeChild(mobileLeft.firstChild);
    }

    mobileLeft.appendChild(helpButtons);
    mobileLeft.appendChild(Mediator.centerMenu.tabSelectList);
    mobileLeft.appendChild(Mediator.leftPanel.domNode);

    if (!document.getElementById('mobileCountyName')) {
      // Normal display name based on display name and full state name
      const curMobileDisplayName =
        curEntityDisplayName + '<br /> ' + Mediator.serverData.StateFull;

      const mobileCountyName = document.createElement('div');
      mobileCountyName.innerHTML = curMobileDisplayName;
      mobileCountyName.id = 'mobileCountyName';

      mobileLeft.insertBefore(
        mobileCountyName,
        mobileLeft.firstChild.nextSibling
      );
    }

    let mobileRight = document.getElementById(mobileRightContainer);

    if (!mobileRight) {
      mobileRight = document.createElement('div');
      mobileRight.id = mobileRightContainer;
      mobileRight.classList.add('mobilePanel');
      document.body.appendChild(mobileRight);
    }

    const searchBar = document.getElementById('loginButtonContainer');

    while (mobileRight.firstChild) {
      mobileRight.removeChild(mobileRight.firstChild);
    }

    const max = Number.parseInt(getComputedStyle(mobileRight)['maxWidth']);
    const sw = window.innerWidth > max ? -1 * max : -1 * window.innerWidth;

    mobileRight.appendChild(searchBar);
    mobileRight.style.right = sw + 'px';

    let toolsButton = document.getElementById('toolsButton');

    if (!toolsButton) {
      toolsButton = document.createElement('button');
      toolsButton.id = 'toolsButton';
      document.body.appendChild(toolsButton);
    }

    let hideButton = document.getElementById('hideToolsButton');

    if (!hideButton) {
      hideButton = document.createElement('button');
      hideButton.id = 'hideToolsButton';
      mobileLeft.insertBefore(hideButton, mobileLeft.firstChild);
    }

    let clearButton = document.getElementById('searchClearButton');

    if (!clearButton) {
      clearButton = document.createElement('button');
      clearButton.id = 'searchClearButton';
      document.body.appendChild(clearButton);
    }

    let mobileSearchButton = document.getElementById('mobileSearchButton');

    if (!mobileSearchButton) {
      mobileSearchButton = document.createElement('button');
      mobileSearchButton.id = 'mobileSearchButton';
      document.body.appendChild(mobileSearchButton);
    }

    let searchHideButton = document.getElementById('hideSearchButton');

    if (!searchHideButton) {
      searchHideButton = document.createElement('button');
      searchHideButton.textContent = 'Hide';
      searchHideButton.id = 'hideSearchButton';
      mobileRight.insertBefore(searchHideButton, mobileRight.firstChild);
    }

    if (!Mediator.mobileLeftVisible) {
      toolsButton.style.visibility = 'visible';
    }

    toolsButton.addEventListener('click', function () {
      showMobileLeft();
    });

    hideButton.addEventListener('click', function () {
      hideMobileLeft(Mediator);
    });

    clearButton.addEventListener('click', function () {
      // @ts-ignore
      Mediator.toolsManager.buttonHandler(null, 'Clear_');
    });

    mobileSearchButton.addEventListener('click', function () {
      if (!Mediator.mobileSearchVisible) {
        showMobileRight();
      }
    });

    searchHideButton.addEventListener('click', function () {
      hideMobileRight();
    });

    if (Mediator.showDesktopOption) {
      let desktopButton = document.getElementById('desktopModeButton');

      if (!desktopButton) {
        desktopButton = document.createElement('button');
        desktopButton.textContent = 'Desktop Mode';
        desktopButton.id = 'desktopModeButton';
        document.body.appendChild(desktopButton);
      }

      desktopButton.addEventListener('click', function () {
        Mediator.desktopMode = true;
        Mediator.tabletMode = false;
        Mediator.inMobileMode = false;
        revertToDesktop();
      });
    }
  }

  function revertToDesktop () {
    // @ts-ignore
    // Set in Views/Shared/Layout.cshtml
    window.setTabContainerWidth();

    Mediator.inMobileMode = false;

    if (Mediator.helppage) {
      Mediator.helppage.showDesktopTabs(true);
    }

    Mediator.centerMenu.domNode.classList.remove('mobile');

    const helpTabContainer = document.getElementById('helpTabsContainer');

    if (helpTabContainer) {
      helpTabContainer.classList.remove('mobile');
    }

    document.getElementById('logo').style.visibility = 'visible';
    document.getElementById('BottomRightMenu').style.visibility = 'visible';

    const desktopMobileButton = document.getElementById('desktopModeButton');

    if (desktopMobileButton) {
      desktopMobileButton.remove();
    }

    if (Mediator.serverData.EntityNameDisplayType === 'BelowToolbar') {
      const searchContainerMain = document.getElementById(
        'searchContainerMain'
      );
      const displayNameLabel = document.getElementById('displayNameLabel');

      searchContainerMain.insertBefore(
        displayNameLabel,
        searchContainerMain.firstChild
      );

      displayNameLabel.parentNode.insertBefore(
        document.getElementById('loginButtonContainer'),
        displayNameLabel.nextSibling
      );
    } else {
      const toolsContainerMain = document.getElementById('toolsContainerMain');
      toolsContainerMain.parentNode.insertBefore(
        document.getElementById('loginButtonContainer'),
        toolsContainerMain.nextSibling
      );
    }

    document
      .getElementById('tabSelectContainer')
      .appendChild(Mediator.centerMenu.tabSelectList);

    document.body.appendChild(document.getElementById('left-panel'));

    document
      .getElementById('countyTabsContainer')
      .appendChild(document.getElementById('helpButtonContainer'));

    document.getElementById(mobileLeftContainer).remove();
    document.getElementById(mobileRightContainer).remove();
    document.getElementById('toolsButton').remove();
    document.getElementById('mobileSearchButton').remove();
    document.getElementById('searchClearButton').remove();

    document
      .getElementById('loadingContainerMain')
      .classList.remove('mobileMode');

    if (Mediator.showTabletOption) {
      addTabletMode();
    }

    if (window.innerWidth >= 1024 && Mediator.showTabletOption) {
      Mediator.showTabletOption = false;
      removeTabletMode();
    }
  }

  function hideMobileLeft (_unused, hideTools = false) {
    Mediator.mobileLeftVisible = false;

    const mobileLeft = document.getElementById(mobileLeftContainer);
    const w =
      (Number.parseInt(getComputedStyle(mobileLeft)['maxWidth']) + 15) * -1;

    if (mobileLeft) {
      mobileLeft.style.webkitTransform = getTranslate(w);
      mobileLeft.style.transform = getTranslate(w);
    }

    document.getElementById('toolsButton').style.visibility = 'hidden';

    if (!hideTools) {
      setTimeout(function () {
        document.getElementById('toolsButton').style.visibility = 'visible';
      }, 100);
    }
  }

  function showMobileLeft () {
    Mediator.mobileLeftVisible = true;

    if (Mediator.mobileSearchVisible) {
      hideMobileRight();
    }

    const mobileLeft = document.getElementById(mobileLeftContainer);
    const w = Number.parseInt(getComputedStyle(mobileLeft)['width']);

    if (mobileLeft) {
      mobileLeft.style.webkitTransform = getTranslate(w);
      mobileLeft.style.transform = getTranslate(w);
    }

    document.getElementById('toolsButton').style.visibility = 'hidden';
  }

  function hideMobileRight () {
    if (document.getElementById('desktopModeButton')) {
      document.getElementById('desktopModeButton').style.visibility = 'visible';
    }

    Mediator.mobileSearchVisible = false;
    document.getElementById('toolsButton').style.visibility = 'visible';

    const mobileRight = document.getElementById(mobileRightContainer);

    const w = window.innerWidth > 407 ? 407 : window.innerWidth;

    if (mobileRight) {
      mobileRight.style.webkitTransform = getTranslate(w);
      mobileRight.style.transform = getTranslate(w);
    }

    document.getElementById('mobileSearchButton').style.visibility = 'visible';
  }

  function showMobileRight () {
    if (document.getElementById('desktopModeButton')) {
      document.getElementById('desktopModeButton').style.visibility = 'hidden';
    }

    if (Mediator.mobileLeftVisible) {
      hideMobileLeft(Mediator, true);
    }

    Mediator.mobileSearchVisible = true;

    const mobileRight = document.getElementById(mobileRightContainer);
    const w = window.innerWidth > 407 ? -407 : -1 * window.innerWidth;
    document.getElementById('toolsButton').style.visibility = 'hidden';

    if (mobileRight) {
      mobileRight.style.webkitTransform = getTranslate(w);
      mobileRight.style.transform = getTranslate(w);
    }

    document.getElementById('mobileSearchButton').style.visibility = 'hidden';
  }

  function getTranslate (w) {
    return 'translateX(' + w.toString() + 'px)';
  }

  function showDesktopMessage () {
    // checks if warning is disabled
    if (
      getValue('disableSizeWarning') ||
      window.location.host === 'localhost'
    ) {
      return;
    }

    if (Mediator.desktopMessageTimer) {
      clearTimeout(Mediator.desktopMessageTimer);
    }

    const constants = {
      screenSizeWarning:
        'Based on the size of your current browser window, ' +
        'you may benefit by using the mobile version of the website. ' +
        'To enable the mobile version, ' +
        'click the \'Mobile Layout\' button on your screen. ' +
        'To revert back to the desktop layout, ' +
        'click the \'Desktop Mode\' button.',

      screenSizeMessage:
        'Based on the size of your current window, ' +
        'you are seeing the mobile version ' +
        'of the site which is designed to accommodate smaller screens. ' +
        'In the event you are using a desktop window that has been minimized,' +
        ' you may view the desktop version of the site ' +
        'by maximizing the size of your window.'
    };

    Mediator.desktopMessageTimer = setTimeout(() => {
      if (Mediator.showDesktopWarning) {
        // if true create first warning (tablet)
        screenSizeWarning(constants.screenSizeWarning);
      } else {
        // else create second warning (mobile)
        screenSizeWarning(constants.screenSizeMessage);
      }

      Mediator.showDesktopWarning = false;
      Mediator.showDesktopMessage = false;
    }, 600);
  }

  function _destroyWidgets () {
    Mediator.gWindowManager.closeAllWindows();
    Mediator.destroyStoredWidgets();

    const curWidgetsIds = _getCurRegisteredWidgetsIds();

    for (let i = 0, len = curWidgetsIds.length; i < len; i++) {
      const curWidget = curWidgetsIds[i];
      const curDijit = dijit.registry.byId(curWidget);

      if (Mediator.startupWidgetsIds.indexOf(curWidget) < 0 && curDijit) {
        Mediator.destroyWidget(curDijit);
      }
    }
  }

  function needsDisclaimer () {
    if (!Mediator.disclaimer && Mediator.serverData.HelpBoxInfos.length > 0) {
      for (let i = 0; i < Mediator.serverData.HelpBoxInfos.length; i++) {
        if (Mediator.serverData.HelpBoxInfos[i].Title === 'Disclaimer') {
          return true;
        }
      }
    }

    return false;
  }

  function _loadGWindowManager () {
    const gWinManager = new GWindowManager();
    document.body.appendChild(gWinManager.domNode);
    return gWinManager;
  }

  function _loadMap () {
    const view = new View({
      // constrainResolution true causes issue on Apple computers
      constrainResolution: false,
      center: fromLonLat([-99, 42]),
      zoom: 6
    });

    view.set('getScale', function () {
      const standardScales = [
        591657527.591555,
        295828763.795777,
        147914381.897889,
        73957190.948944,
        36978595.474472,
        18489297.737236,
        9244648.868618,
        4622324.434309,
        2311162.217155,
        1155581.108577,
        577790.554289,
        288895.277144,
        144447.638572,
        72223.819286,
        36111.909643,
        18055.954822,
        9027.977411,
        4513.988705,
        2256.994353,
        1128.497176,
        564.248588,
        282.124294,
        141.062147,
        70.5310735
      ];

      const scaleAtZoom = standardScales[view.getZoom()];

      if (scaleAtZoom) {
        return scaleAtZoom;
      }

      const inchesPerMeter = 39.37;
      const dpi = 96;

      return inchesPerMeter * dpi * view.getResolution();
    });

    const map = new OLMap({
      target: 'map',
      view,
      controls: []
    });

    map.set('previousExtents', []);
    map.set('nextExtents', []);

    map.on('moveend', function () {
      const previousExtents = map.get('previousExtents');
      const currentExtent = map.get('currentExtent');

      if (map.get('skipMoveEnd')) {
        map.set('skipMoveEnd', false);
        return;
      }

      map.set('nextExtents', []);

      if (previousExtents.length === 10) {
        previousExtents.pop();
      }

      if (currentExtent) {
        previousExtents.push(currentExtent);
      }

      map.set('currentExtent', map.getView().calculateExtent());
    });

    return map;
  }

  function _bootUpAlwaysOnIdentify () {
    if (
      !Mediator.toolsManager.hasTool(
        'AlwaysOnIdentify',
        Mediator.getCurrentTabName()
      )
    ) {
      return;
    }

    Mediator.toolsManager
      .getToolWidgetAsync('AlwaysOnIdentify')
      .then(function (widget) {
        widget.bootUp();
        Mediator.AlwaysOnIdentify = widget;
      });
  }

  /**
   * Creates RightMenu and asynchronously loads the SearchBar
   */
  async function _loadRightMenu () {
    const rightMenu = new RightMenu({ id: 'rightMenu' });
    document.body.appendChild(rightMenu.domNode);

    await rightMenu.lazyLoad();

    return rightMenu;
  }

  function _loadLeftPanel () {
    const leftPanel = new GWindowList({
      id: 'left-panel',
      initGWindows: [
        {
          key: 'tools',
          title: 'Tools',
          id: 'toolsWindow',
          content: document.createElement('div')
        },
        {
          key: 'layers',
          title: 'Layers',
          id: 'layersContainer',
          content: document.createElement('div')
        },
        {
          key: 'basemaps',
          title: 'Basemaps',
          id: 'basemapContainer',
          content: document.createElement('div')
        }
      ]
    });

    document.body.appendChild(leftPanel.domNode);

    return leftPanel;
  }

  function _loadLayersManager () {
    return new LayersManager({ id: 'LayersManager' });
  }

  function _loadBaseMapManager () {
    return new BaseMapManager({ id: 'BaseMapManager' });
  }

  function _loadToolManager () {
    return new ToolsManager({ id: 'ToolsManager' });
  }

  function _loadDisclaimer () {
    return Mediator.Factory.Create({ name: 'HelpPage', params: null });
  }

  function _loadBottomRightMenu () {
    const bottomRightMenu = new BottomRightMenu({ id: 'BottomRightMenu' });
    document.body.appendChild(bottomRightMenu.domNode);
    return bottomRightMenu;
  }

  function _loadLoginPanel () {
    const div = document.createElement('div');
    document.body.appendChild(div);
    return Mediator.Factory.Create({
      name: 'LoginPage',
      node: div,
      params: { id: 'loginpage', loginInit: _LoginInit }
    });
  }

  function _loadResultsPanel () {
    return Mediator.Factory.Create({ name: 'ResultsPanel', params: null });
  }

  // For payment panel
  function _loadPayment () {
    return Mediator.Factory.Create({
      name: 'PaymentPanel',
      params: { id: 'paymentPanel' },
      node: document.createElement('div')
    });
  }

  function _loadCart () {
    return Mediator.Factory.Create({
      name: 'CartPanel',
      node: document.createElement('div'),
      params: { id: 'CartPanel' }
    });
  }

  function _getCurRegisteredWidgetsIds () {
    return dijit.registry.toArray().map(d => d.id);
  }
});
